import React, { useEffect, useState } from "react";
import { PageHeader } from "../components/Layout";
import { api, apiErr } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { Wifi, Save, QrCode, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function CaptivePortal() {
  const { t, lang } = useI18n();
  const { effectiveStructure, isSuperAdmin } = useAuth();
  const [structures, setStructures] = useState([]);
  const [sid, setSid] = useState("");
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/structures").then((r) => { setStructures(r.data); setSid(effectiveStructure || r.data[0]?.id || ""); });
  }, [effectiveStructure]);

  useEffect(() => {
    if (!sid) return;
    api.get(`/portal/${sid}`).then((r) => setCfg({ ...r.data, structure_id: sid }));
  }, [sid]);

  const save = async () => {
    try { await api.put(`/portal/${sid}`, cfg); toast.success("✓"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (!cfg) return <div className="text-slate-400">{t("common.loading")}</div>;
  const welcomeKey = `welcome_${lang}`;
  const structName = structures.find((s) => s.id === sid)?.name || "";

  return (
    <div data-testid="portal-page">
      <PageHeader title={t("portal.title")} action={
        <div className="flex gap-2">
          <a data-testid="open-guest-portal" href={`/guest/${sid}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-white/5 hover:bg-white/10 px-4 py-2.5 text-sm font-medium transition-colors"><ExternalLink className="h-4 w-4" /> {t("portal.preview")}</a>
          <button data-testid="save-portal-btn" onClick={save} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2.5 text-sm font-semibold transition-colors"><Save className="h-4 w-4" /> {t("common.save")}</button>
        </div>
      } />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor */}
        <div className="rounded-xl border border-[#1F2937] bg-[#111827] p-6 space-y-4">
          {isSuperAdmin && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("nav.structures")}</label>
              <select data-testid="portal-structure-select" value={sid} onChange={(e) => setSid(e.target.value)} className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm">
                {structures.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          {["it", "en", "fr", "de"].map((l) => (
            <div key={l}>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("portal.welcome")} [{l.toUpperCase()}]</label>
              <input data-testid={`portal-welcome-${l}`} value={cfg[`welcome_${l}`] || ""} onChange={(e) => setCfg({ ...cfg, [`welcome_${l}`]: e.target.value })} className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          ))}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("portal.color")}</label>
              <input data-testid="portal-color" type="color" value={cfg.primary_color} onChange={(e) => setCfg({ ...cfg, primary_color: e.target.value })} className="mt-1 w-full h-10 rounded-lg bg-[#0B0F17] border border-[#1F2937]" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("portal.logo")}</label>
              <input data-testid="portal-logo" value={cfg.logo_url} onChange={(e) => setCfg({ ...cfg, logo_url: e.target.value })} className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("portal.info")}</label>
            <textarea data-testid="portal-info" value={cfg.info_text} onChange={(e) => setCfg({ ...cfg, info_text: e.target.value })} rows={3} className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="portal-show-qr" type="checkbox" checked={cfg.show_qr} onChange={(e) => setCfg({ ...cfg, show_qr: e.target.checked })} className="accent-blue-600" /> {t("portal.show_qr")}
          </label>
        </div>

        {/* Preview */}
        <div className="rounded-xl border border-[#1F2937] bg-[#0B0F17] p-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">{t("portal.preview")}</div>
          <div data-testid="portal-preview" className="rounded-2xl overflow-hidden shadow-2xl" style={{ background: "#0d121c" }}>
            <div className="h-2" style={{ background: cfg.primary_color }} />
            <div className="p-8 text-center">
              {cfg.logo_url ? <img src={cfg.logo_url} alt="logo" className="h-14 mx-auto mb-4 object-contain" /> : <div className="h-14 w-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: cfg.primary_color + "22" }}><Wifi className="h-7 w-7" style={{ color: cfg.primary_color }} /></div>}
              <h2 className="font-display text-xl font-bold text-white">{cfg[welcomeKey] || cfg.welcome_it}</h2>
              <p className="text-sm text-slate-400 mt-1">{structName}</p>
              {cfg.info_text && <p className="text-xs text-slate-500 mt-3">{cfg.info_text}</p>}
              <div className="mt-6">
                <div className="text-xs text-slate-500 mb-1.5">{t("portal.enter_code")}</div>
                <input disabled placeholder="XXXXXX" className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-3 text-center font-mono text-lg tracking-[0.4em] text-white" />
              </div>
              <button className="mt-3 w-full rounded-lg py-3 text-sm font-semibold text-white" style={{ background: cfg.primary_color }}>{t("portal.connect")}</button>
              {cfg.show_qr && <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-500"><QrCode className="h-4 w-4" /> QR Code</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
